import { useEffect, useState, useCallback, useRef } from "react";
import { Copy as _Copy, ExternalLink as _ExternalLink, QrCode as _QrCode } from "lucide-react";
import QRCode from "qrcode";
import { api, formatApiErrorDetail } from "@/api";
import { confirmDialog, promptDialog } from "@/components/Confirm";
import { LocationsTab } from "@/components/LocationsTab";
import { ConceptDoc } from "@/components/ConceptDoc";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { ROLE_LABELS, fmtTime, timeAgo } from "@/lib/constants";
import { MicSelector } from "@/components/MicSelector";
import { BuildingsMapEditor } from "@/components/BuildingsMapEditor";
import { WaterMapEditor } from "@/components/WaterMapEditor";
import { useBranding } from "@/context/BrandingContext";
import { Users, Truck, Building2, Tag, Plus, Trash2, Pencil, X, Clock, Smartphone, History, SlidersHorizontal, Check, Ban, KeyRound, Wifi, Globe, Droplet, ScrollText, Map as MapIcon, Contact, Tv, Printer, PhoneCall, ArrowUp, ArrowDown } from "lucide-react";
import { MapSettings } from "@/components/MapSettings";
import { SharedLayerEditor } from "@/components/SharedLayerEditor";
import { ColorLegendEditor } from "@/components/ColorLegendEditor";
import { ServiceAreaEditor } from "@/components/ServiceAreaEditor";
import { ContactsTab } from "@/components/ContactsTab";
import AuditLog from "@/pages/AuditLog";
import { kenmerkMeta } from "@/lib/kenmerkIcons";
import { ApproachRouteEditor } from "@/components/IncidentMaps";
import { toast } from "sonner";
import { CAQuestionEditor, CASubcatEditor } from "@/components/CallAssistEditors";

function CallAssistTab({ canWrite }) {
  const [enabled, setEnabled] = useState(false);
  const [types, setTypes] = useState([]);
  const [kb, setKb] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/callassist").then(({ data }) => setEnabled(!!data.enabled)).catch(() => {});
    api.get("/incident-types").then(({ data }) => { setTypes(data || []); if (data && data[0]) setTypeId(data[0].id); }).catch(() => {});
    api.get("/kb/articles").then(({ data }) => setKb(data || [])).catch(() => {});
  }, []);
  useEffect(() => { if (typeId) api.get(`/callassist/flow/${typeId}`).then(({ data }) => { setQuestions(data.questions || []); setSubcategories(data.subcategories || []); }).catch(() => {}); }, [typeId]);

  const toggleEnabled = async () => {
    const next = !enabled; setEnabled(next);
    try { await api.put("/settings/callassist", { enabled: next }); toast.success(next ? "CallAssist ingeschakeld" : "CallAssist uitgeschakeld"); }
    catch { setEnabled(!next); toast.error("Opslaan mislukt"); }
  };
  const cleanSubs = (subs) => (subs || []).map((s) => ({ ...s, questions: (s.questions || []).filter((q) => (q.text || "").trim()), subcategories: cleanSubs(s.subcategories) }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/callassist/flow/${typeId}`, { questions: questions.filter((q) => (q.text || "").trim()), subcategories: cleanSubs(subcategories) }); toast.success("Vragenflow opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="callassist-tab">
      <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-bold text-slate-900">CallAssist bij aanmaken</div>
            <div className="text-xs text-slate-500">Toont na het aanmaken van een melding een vragenflow die de centralist helpt.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold ${enabled ? "text-emerald-600" : "text-slate-400"}`}>{enabled ? "Aan" : "Uit"}</span>
            <button type="button" role="switch" aria-checked={enabled} data-testid="callassist-toggle" disabled={!canWrite} onClick={canWrite ? toggleEnabled : undefined}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-300"} disabled:opacity-60`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vragenflow per type</span>
            <select data-testid="callassist-type-select" value={typeId} onChange={(e) => setTypeId(e.target.value)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-semibold text-slate-700">
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {canWrite && <button data-testid="callassist-save" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60">{saving ? "Opslaan…" : "Flow opslaan"}</button>}
        </div>

        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Algemene vragen (altijd gesteld)</div>
        <CAQuestionEditor questions={questions} onChange={setQuestions} kb={kb} prefix="gen" />
        <div className="mt-6 border-t border-slate-200 pt-4" data-testid="callassist-subcats">
          <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500"><Tag className="h-3.5 w-3.5" /> Subcategorieën (geneste niveaus)</div>
          <p className="mb-3 text-xs text-slate-400">Bv. Brand → Buitenbrand → Autobrand. Elke subcategorie heeft eigen vragen; de centralist kiest tijdens de melding het niveau. Niveaus kunnen onbeperkt genest worden.</p>
          <CASubcatEditor subs={subcategories} onChange={setSubcategories} kb={kb} depth={0} />
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "users", label: "Gebruikers", icon: Users },
  { key: "vehicles", label: "Voertuigen", icon: Truck },
  { key: "locaties", label: "Locaties", icon: MapIcon },
  { key: "contacten", label: "Contacten", icon: Contact },
  { key: "buildings", label: "Gebouwen", icon: Building2 },
  { key: "bluswater", label: "Bluswater", icon: Droplet },
  { key: "kaart", label: "Kaart", icon: MapIcon },
  { key: "types", label: "Incidenttypes", icon: Tag },
  { key: "apparaten", label: "Apparaten", icon: Smartphone },
  { key: "dienst", label: "Dienst", icon: Clock },
  { key: "instellingen", label: "Instellingen", icon: SlidersHorizontal },
  { key: "callassist", label: "CallAssist", icon: PhoneCall },
  { key: "omgevingen", label: "Omgevingen", icon: Globe },
  { key: "audit", label: "Logboek & audit", icon: ScrollText },
];

function Field({ label, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <input {...props} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
    </div>
  );
}

function QrImg({ text }) {
  const [url, setUrl] = useState("");
  useEffect(() => { QRCode.toDataURL(text, { width: 150, margin: 1 }).then(setUrl).catch(() => {}); }, [text]);
  return url ? <img src={url} alt="QR" data-testid="pairing-qr" className="h-28 w-28 rounded-lg border border-slate-200" /> : null;
}

function ShiftSettings({ canWrite }) {
  const [shift, setShift] = useState({ start: "08:00", end: "08:00" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/shift").then(({ data }) => setShift(data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/settings/shift", shift); toast.success("Diensttijden opgeslagen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="shift-settings">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Clock style={{ width: 16, height: 16 }} /> Diensttijden</div>
      <p className="mt-1 text-sm text-slate-500">Bepaal wanneer een dienst begint en eindigt. Standaard 08:00 tot 08:00 de volgende dag. Dit stuurt de "Dienst (vandaag)"-periode op het dashboard aan.</p>
      <form onSubmit={save} className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Begintijd</label>
            <input data-testid="shift-start" type="time" required disabled={!canWrite} value={shift.start} onChange={(e) => setShift({ ...shift, start: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 disabled:bg-slate-50" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Eindtijd</label>
            <input data-testid="shift-end" type="time" required disabled={!canWrite} value={shift.end} onChange={(e) => setShift({ ...shift, end: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 disabled:bg-slate-50" />
          </div>
        </div>
        {canWrite && <button data-testid="shift-save" type="submit" disabled={saving} className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">Opslaan</button>}
      </form>
    </div>
  );
}

function AckPolicySettings({ canWrite }) {
  const [pol, setPol] = useState({ require_vehicle_ack: true, require_pager_ack: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/ack-policy").then(({ data }) => setPol(data)).catch(() => {}); }, []);
  const save = async (next) => {
    setPol(next); setSaving(true);
    try { await api.put("/settings/ack-policy", next); toast.success("Bevestig-instellingen opgeslagen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const Toggle = ({ on, onClick, testid }) => (
    <button type="button" data-testid={testid} disabled={!canWrite || saving} onClick={onClick}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"} disabled:opacity-60`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
  return (
    <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="ack-policy-settings">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Check style={{ width: 16, height: 16 }} /> Bevestigen van uitruk</div>
      <p className="mt-1 text-sm text-slate-500">Bepaal welke apparaten een uitruk moeten bevestigen. Bevestigingen verschijnen als "Bevestigd door" bij de centralist, op de kazernemonitor en op de pager. Bij een pager wordt de aan de pager gekoppelde naam vermeld.</p>
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3">
          <div><div className="text-sm font-semibold text-slate-800">Voertuigen moeten bevestigen</div><div className="text-xs text-slate-500">Toont "Bevestig uitruk" in de voertuig-app</div></div>
          <Toggle testid="ack-toggle-vehicle" on={pol.require_vehicle_ack} onClick={() => save({ ...pol, require_vehicle_ack: !pol.require_vehicle_ack })} />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3">
          <div><div className="text-sm font-semibold text-slate-800">Pagers moeten bevestigen</div><div className="text-xs text-slate-500">De aan de pager gekoppelde naam wordt vermeld bij de bevestiging</div></div>
          <Toggle testid="ack-toggle-pager" on={pol.require_pager_ack} onClick={() => save({ ...pol, require_pager_ack: !pol.require_pager_ack })} />
        </div>
      </div>
    </div>
  );
}

function EnvironmentsTab({ canWrite }) {
  const [envs, setEnvs] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [urlModal, setUrlModal] = useState(null);
  const [opts, setOpts] = useState({ copy_kb: true, copy_incident_types: true, copy_vehicles: false });
  const load = useCallback(async () => { try { const { data } = await api.get("/environments"); setEnvs(data || []); } catch (e) {} }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!name.trim()) { toast.error("Vul een naam in"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/environments", { name: name.trim(), ...opts });
      const c = data.copied || {};
      toast.success(`Omgeving "${data.name}" aangemaakt — gekopieerd: ${c.kb || 0} kennisbank, ${c.incident_types || 0} types, ${c.vehicles || 0} voertuigen`);
      setName(""); load();
    }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const optRow = (key, label) => (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" data-testid={`env-opt-${key}`} checked={opts[key]} onChange={(e) => setOpts((o) => ({ ...o, [key]: e.target.checked }))} className="h-4 w-4" />
      {label}
    </label>
  );
  const remove = async (env) => {
    if (!(await confirmDialog({ title: "Omgeving verwijderen", message: `Omgeving "${env.name}" en alle bijbehorende data verwijderen?`, danger: true, confirmText: "Verwijderen" }))) return;
    try { await api.delete(`/environments/${env.id}`); toast.success("Omgeving verwijderd"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const copy = (t) => { try { navigator.clipboard?.writeText(t); toast.success("Gekopieerd"); } catch (e) {} };
  return (
    <div className="space-y-5" data-testid="environments-tab">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Globe style={{ width: 16, height: 16 }} /> Omgevingen (multi-tenant)</div>
        <p className="mt-1 text-sm text-slate-500">Maak volledig gescheiden omgevingen met een eigen, niet-raadbare code. Apparaten koppelen via <span className="font-mono">/o/CODE/pager</span>, <span className="font-mono">/voertuig</span> of <span className="font-mono">/kazerne</span>. Bij aanmaken wordt de kennisbank gekopieerd. De hoofdomgeving kan niet worden verwijderd.</p>
        {canWrite && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <input data-testid="env-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam nieuwe omgeving, bijv. België"
                className="min-w-[14rem] flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              <button data-testid="create-env-btn" onClick={create} disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"><Plus className="h-4 w-4" /> Omgeving aanmaken</button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Kopieer uit hoofdomgeving:</span>
              {optRow("copy_kb", "Kennisbank")}
              {optRow("copy_incident_types", "Incidenttypes")}
              {optRow("copy_vehicles", "Voertuigen")}
            </div>
          </div>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {envs.map((e) => (
          <div key={e.id} data-testid={`env-${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-900">{e.name}{e.is_master && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Hoofdomgeving</span>}</div>
                <div className="mt-1 font-mono text-lg font-black tracking-widest text-blue-700">{e.code}</div>
              </div>
              {!e.is_master && canWrite && <button data-testid={`delete-env-${e.id}`} onClick={() => remove(e)} className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <button data-testid={`env-urls-${e.id}`} onClick={() => setUrlModal(e)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"><Smartphone className="h-3.5 w-3.5" /> Toon device-URLs</button>
          </div>
        ))}
      </div>
      {urlModal && (
        <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/50 p-4" onClick={() => setUrlModal(null)}>
          <div onClick={(ev) => ev.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" data-testid="env-urls-modal">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Device-URLs — {urlModal.name} ({urlModal.code})</div>
              <button onClick={() => setUrlModal(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2">
              {[["Pager", urlModal.urls.pager], ["Voertuig", urlModal.urls.voertuig], ["Kazernemonitor", urlModal.urls.kazerne]].map(([lbl, url]) => (
                <div key={lbl} className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{lbl}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">{url}</code>
                    <button onClick={() => copy(url)} className="shrink-0 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100">Kopieer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEVICE_STATUS = {
  pending: { label: "In afwachting", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Goedgekeurd", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  revoked: { label: "Ingetrokken", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function DeviceUrl({ token, path }) {
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState("");
  const url = token ? `${window.location.origin}/${path}?token=${token}` : "";
  useEffect(() => { if (showQr && url) QRCode.toDataURL(url, { width: 260, margin: 1 }).then(setQr).catch(() => {}); }, [showQr, url]);
  if (!token) return null;
  return (
    <div className="mt-1.5" data-testid="device-url">
      <div className="flex items-center gap-1">
        <input readOnly value={url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 truncate rounded border border-slate-200 bg-white px-2 py-1 font-mono text-[11px] text-slate-600" />
        <button data-testid="device-url-copy" title="Kopieer URL" onClick={() => { navigator.clipboard.writeText(url); toast.success("URL gekopieerd"); }} className="rounded p-1 text-slate-500 hover:bg-slate-100"><_Copy className="h-3.5 w-3.5" /></button>
        <button data-testid="device-url-open" title="Open in nieuw venster" onClick={() => window.open(url, "_blank")} className="rounded p-1 text-slate-500 hover:bg-slate-100"><_ExternalLink className="h-3.5 w-3.5" /></button>
        <button data-testid="device-url-qr-toggle" title="Toon QR om te installeren" onClick={() => setShowQr((v) => !v)} className={`rounded p-1 hover:bg-slate-100 ${showQr ? "text-blue-600" : "text-slate-500"}`}><_QrCode className="h-3.5 w-3.5" /></button>
      </div>
      {showQr && qr && (
        <div className="mt-2 flex items-center gap-3">
          <img src={qr} alt="QR" data-testid="device-url-qr-img" className="h-40 w-40 rounded-lg border border-slate-200 bg-white p-1.5" />
          <span className="text-[11px] text-slate-500">Scan met het toestel om direct te installeren en automatisch in te loggen.</span>
        </div>
      )}
    </div>
  );
}

function DevicesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [codes, setCodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [vehId, setVehId] = useState("");
  const [monCodes, setMonCodes] = useState([]);
  const [monDevices, setMonDevices] = useState([]);
  const [locs, setLocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [monLoc, setMonLoc] = useState("");
  const [pagerCodes, setPagerCodes] = useState([]);
  const [pagerDevices, setPagerDevices] = useState([]);
  const [pagerLoc, setPagerLoc] = useState("");
  const [pagerLabel, setPagerLabel] = useState("");

  const load = useCallback(async () => {
    const [v, c, d] = await Promise.all([api.get("/vehicles"), api.get("/pairing-codes"), api.get("/devices")]);
    setVehicles(v.data); setCodes(c.data); setDevices(d.data);
    try {
      const [mc, md, lc] = await Promise.all([api.get("/monitor/pairing-codes"), api.get("/monitor/devices"), api.get("/locations")]);
      setMonCodes(mc.data); setMonDevices(md.data); setLocs(lc.data);
      const [pc, pd] = await Promise.all([api.get("/pager/pairing-codes"), api.get("/pager/devices")]);
      setPagerCodes(pc.data); setPagerDevices(pd.data);
      const uu = await api.get("/users"); setUsers(uu.data || []);
    } catch (_) {}
    if (!vehId && v.data[0]) setVehId(v.data[0].id);
  }, [vehId]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const genCode = async () => {
    try { await api.post("/pairing-codes", { vehicle_id: vehId }); toast.success("Koppelcode aangemaakt"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delCode = async (id) => { try { await api.delete(`/pairing-codes/${id}`); load(); } catch (e) {} };
  const approve = async (id) => { try { await api.post(`/devices/${id}/approve`); toast.success("Apparaat goedgekeurd"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const revoke = async (id) => { if (!(await confirmDialog({ title: "Apparaat ontkoppelen", message: "Weet je zeker dat je dit apparaat wilt ontkoppelen?", danger: true, confirmText: "Ontkoppel" }))) return; try { await api.post(`/devices/${id}/revoke`); toast.success("Apparaat ontkoppeld"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const genMonCode = async () => { try { await api.post("/monitor/pairing-codes", { location_id: monLoc || null }); toast.success("Monitor-code aangemaakt"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const delMonCode = async (id) => { try { await api.delete(`/monitor/pairing-codes/${id}`); load(); } catch (e) {} };
  const revokeMon = async (id) => { if (!(await confirmDialog({ title: "Monitor ontkoppelen", message: "Weet je zeker dat je deze monitor wilt ontkoppelen?", danger: true, confirmText: "Ontkoppel" }))) return; try { await api.post(`/monitor/devices/${id}/revoke`); toast.success("Monitor ontkoppeld"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const genPagerCode = async () => { if (!pagerLabel.trim()) { toast.error("Vul een naam of functie in (bijv. Bevelvoerder Noord)"); return; } try { await api.post("/pager/pairing-codes", { label: pagerLabel.trim(), location_id: pagerLoc || null }); toast.success("Pager-code aangemaakt"); setPagerLabel(""); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const delPagerCode = async (id) => { try { await api.delete(`/pager/pairing-codes/${id}`); load(); } catch (e) {} };
  const revokePager = async (id) => { if (!(await confirmDialog({ title: "Pager ontkoppelen", message: "Weet je zeker dat je deze pager wilt ontkoppelen?", danger: true, confirmText: "Ontkoppel" }))) return; try { await api.post(`/pager/devices/${id}/revoke`); toast.success("Pager ontkoppeld"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const renamePager = async (m) => { const name = await promptDialog({ title: "Pager-naam", message: "Persoons-/functienaam voor deze pager (bijv. Bevelvoerder Noord):", defaultValue: m.label || "", confirmText: "Opslaan" }); if (name === null) return; try { await api.put(`/pager/devices/${m.id}`, { label: name.trim() || "Pager" }); toast.success("Pager-naam bijgewerkt"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const deletePager = async (m) => { if (!(await confirmDialog({ title: "Pager verwijderen", message: `"${m.label}" definitief uit de lijst verwijderen?`, danger: true, confirmText: "Verwijderen" }))) return; try { await api.delete(`/pager/devices/${m.id}`); toast.success("Pager verwijderd"); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  const origin = window.location.origin;

  return (
    <div className="space-y-6" data-testid="devices-tab">
      {/* App install QR codes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="app-install-qr">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Smartphone style={{ width: 16, height: 16 }} /> Apps installeren (scan QR)</div>
        <p className="mt-1 text-sm text-slate-500">Scan met de telefoon of tablet om de app te openen. Voeg daarna toe aan het startscherm (&ldquo;Zet op beginscherm&rdquo;) voor een echt app-icoon.</p>
        <button data-testid="open-install-overview" onClick={() => window.open("/beheer/installatie", "_blank")} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Printer className="h-4 w-4" /> Installatie-overzicht (alle QR-codes) printen</button>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            { key: "pager", label: "Pager", path: "/pager", desc: "Alarmering (piket)" },
            { key: "kazerne", label: "Kazernemonitor", path: "/kazerne", desc: "Groot scherm in de kazerne" },
            { key: "voertuig", label: "Voertuig-app", path: "/voertuig", desc: "Tablet in het voertuig" },
          ].map((a) => (
            <div key={a.key} data-testid={`app-install-${a.key}`} className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <QrImg text={`${origin}${a.path}`} />
              <div className="text-sm font-bold text-slate-900">{a.label}</div>
              <div className="text-[11px] text-slate-400">{a.desc}</div>
              <a href={a.path} target="_blank" rel="noreferrer" className="break-all font-mono text-[11px] text-blue-600 hover:underline">{origin}{a.path}</a>
            </div>
          ))}
        </div>
      </div>

      {/* Generate one-time code */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><KeyRound style={{ width: 16, height: 16 }} /> Nieuwe koppelcode (éénmalig, per device)</div>
        <p className="mt-1 text-sm text-slate-500">Genereer een unieke code voor een voertuig. Het device voert de code in óf scant de QR. Daarna moet het apparaat hieronder handmatig worden goedgekeurd.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="code-vehicle-select" value={vehId} onChange={(e) => setVehId(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.callsign} · {v.name}</option>)}
          </select>
          <button data-testid="generate-code" onClick={genCode} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Genereer code</button>
        </div>

        {codes.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {codes.map((c) => (
              <div key={c.id} data-testid={`code-${c.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <QrImg text={`${origin}/voertuig?code=${c.code}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-lg font-bold tracking-widest text-slate-900">{c.code}</div>
                  <div className="text-xs text-slate-500">{c.vehicle?.callsign} · {c.vehicle?.name}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Ongebruikt · {timeAgo(c.created_at)}</div>
                  <button data-testid={`del-code-${c.id}`} onClick={() => delCode(c.id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Intrekken</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Kazernemonitor pairing */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="monitor-codes-card">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Tv style={{ width: 16, height: 16 }} /> Kazernemonitor koppelcode</div>
        <p className="mt-1 text-sm text-slate-500">Genereer een code voor een kazernemonitor-scherm. Open <span className="font-mono">/kazerne</span> op de monitor en voer de code in (of scan de QR). De monitor draait daarna zonder personeelslogin.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="mon-code-loc" value={monLoc} onChange={(e) => setMonLoc(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500"><option value="">— locatie (optioneel) —</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
          <button data-testid="generate-monitor-code" onClick={genMonCode} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Genereer monitor-code</button>
        </div>
        {monCodes.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {monCodes.map((c) => (
              <div key={c.id} data-testid={`mon-code-${c.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <QrImg text={`${origin}/kazerne?code=${c.code}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-lg font-bold tracking-widest text-slate-900">{c.code}</div>
                  <div className="text-xs text-slate-500">{c.label}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Ongebruikt · {timeAgo(c.created_at)}</div>
                  <button data-testid={`del-mon-code-${c.id}`} onClick={() => delMonCode(c.id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Intrekken</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {monDevices.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Gekoppelde monitors ({monDevices.length})</div>
            {monDevices.map((m) => (
              <div key={m.id} data-testid={`mon-device-${m.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{m.label} <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{m.status === "approved" ? "Actief" : "Ontkoppeld"}</span></div>
                  <div className="text-[11px] text-slate-500">{m.location_name ? `Locatie: ${m.location_name}` : "Geen locatie"} · ID {m.id.slice(0, 8)} · laatst gezien {timeAgo(m.last_seen)}</div>
                  {m.status === "approved" && <DeviceUrl token={m.token} path="kazerne" />}
                </div>
                {m.status === "approved" && (
                  <div className="flex shrink-0 items-center gap-2">
                    <select data-testid={`mon-loc-${m.id}`} defaultValue={m.location_id || ""} onChange={(e) => api.post(`/monitor/devices/${m.id}/location`, { location_id: e.target.value || null }).then(() => { toast.success("Locatie opgeslagen"); load(); }).catch(() => {})} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700">
                      <option value="">Geen locatie</option>
                      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <select data-testid={`mon-idle-${m.id}`} defaultValue={m.idle_mode || ""} onChange={(e) => api.post(`/monitor/devices/${m.id}/idle-mode`, { idle_mode: e.target.value }).then(() => toast.success("Idle-modus opgeslagen")).catch(() => {})} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700">
                      <option value="">Idle: standaard</option>
                      <option value="time">Idle: Tijd</option>
                      <option value="tasks">Idle: Taken</option>
                    </select>
                    <button data-testid={`revoke-mon-${m.id}`} onClick={() => revokeMon(m.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"><Ban className="h-3.5 w-3.5" /> Ontkoppel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pager pairing */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pager-codes-card">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><KeyRound style={{ width: 16, height: 16 }} /> Pager koppelcode</div>
        <p className="mt-1 text-sm text-slate-500">Genereer een code voor een pager (telefoon). Geef een duidelijke persoons-/functienaam op (bijv. "Bevelvoerder Noord") — deze verschijnt bij de bevestiging op elk scherm. Open <span className="font-mono">/pager</span> op het toestel en voer de code in (of scan de QR). De pager alarmeert daarna op de gekoppelde locatie — zonder personeelslogin.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <datalist id="pager-name-suggest">
            {users.map((u) => <option key={`u-${u.id}`} value={u.name} />)}
            {locs.map((l) => <option key={`l-${l.id}`} value={l.name} />)}
          </datalist>
          <input data-testid="pager-code-name" list="pager-name-suggest" value={pagerLabel} onChange={(e) => setPagerLabel(e.target.value)} placeholder="Naam / functie of locatie — typ voor suggesties"
            className="min-w-[16rem] flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
          <select data-testid="pager-code-loc" value={pagerLoc} onChange={(e) => setPagerLoc(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500"><option value="">— locatie (optioneel) —</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
          <button data-testid="generate-pager-code" onClick={genPagerCode} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Genereer pager-code</button>
        </div>
        {pagerCodes.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {pagerCodes.map((c) => (
              <div key={c.id} data-testid={`pager-code-${c.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <QrImg text={`${origin}/pager?code=${c.code}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-lg font-bold tracking-widest text-slate-900">{c.code}</div>
                  <div className="text-xs text-slate-500">{c.label}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Ongebruikt · {timeAgo(c.created_at)}</div>
                  <button data-testid={`del-pager-code-${c.id}`} onClick={() => delPagerCode(c.id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Intrekken</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {pagerDevices.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Gekoppelde pagers ({pagerDevices.length})</div>
            {pagerDevices.map((m) => (
              <div key={m.id} data-testid={`pager-device-${m.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{m.label} <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{m.status === "approved" ? "Actief" : "Ontkoppeld"}</span></div>
                  <div className="text-[11px] text-slate-500">{m.location_name ? `Locatie: ${m.location_name}` : "Persoonspager (geen locatie)"} · ID {m.id.slice(0, 8)} · laatst gezien {timeAgo(m.last_seen)}</div>
                  {m.status === "approved" && <DeviceUrl token={m.token} path="pager" />}
                </div>
                {m.status === "approved" ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <select data-testid={`pager-loc-${m.id}`} defaultValue={m.location_id || ""} onChange={(e) => api.put(`/pager/devices/${m.id}`, { location_id: e.target.value || null }).then(() => { toast.success("Locatie opgeslagen"); load(); }).catch(() => {})} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-700">
                      <option value="">Geen locatie</option>
                      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <button data-testid={`rename-pager-${m.id}`} onClick={() => renamePager(m)} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"><Pencil className="h-3.5 w-3.5" /> Naam</button>
                    <button data-testid={`revoke-pager-${m.id}`} onClick={() => revokePager(m.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"><Ban className="h-3.5 w-3.5" /> Ontkoppel</button>
                  </div>
                ) : (
                  <button data-testid={`delete-pager-${m.id}`} onClick={() => deletePager(m)} className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /> Verwijder</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connected devices */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900"><Smartphone style={{ width: 16, height: 16 }} /> Geconnecteerde devices ({devices.length})</div>
        {devices.length === 0 ? <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nog geen apparaten aangemeld</div> : (
          <div className="space-y-3">
            {devices.map((d) => {
              const st = DEVICE_STATUS[d.status] || DEVICE_STATUS.revoked;
              return (
                <div key={d.id} data-testid={`device-${d.id}`} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{d.label}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="mt-1 grid gap-0.5 text-[11px] text-slate-500">
                      <div>Device-ID: <span className="font-mono">{d.id.slice(0, 8)}</span> · Voertuig: {d.vehicle?.callsign || "—"}</div>
                      <div className="truncate flex items-center gap-1"><Globe style={{ width: 11, height: 11 }} /> Aangemeld IP: {d.enroll_ip || "—"} · Laatste IP: {d.last_ip || "—"}</div>
                      <div className="truncate">Device-info: {d.device_info ? d.device_info.slice(0, 60) : "—"}</div>
                      <div>Aangemeld: {fmtTime(d.created_at)} · Laatste login: {d.last_login ? fmtTime(d.last_login) : "—"} · Laatst gezien: {timeAgo(d.last_seen)}</div>
                      {d.approved_by && <div>Goedgekeurd door: {d.approved_by} ({fmtTime(d.approved_at)})</div>}
                    </div>
                    {d.status === "approved" && <DeviceUrl token={d.token} path="voertuig" />}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {d.status !== "approved" && <button data-testid={`approve-${d.id}`} onClick={() => approve(d.id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"><Check className="h-3.5 w-3.5" /> Approve</button>}
                    {d.status !== "revoked" && <button data-testid={`revoke-${d.id}`} onClick={() => revoke(d.id)} className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100"><Ban className="h-3.5 w-3.5" /> Ontkoppel</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const EVENT_CLS = {
  "gebruiker login": "bg-blue-50 text-blue-700",
  "apparaat login": "bg-emerald-50 text-emerald-700",
  "apparaat aangemeld": "bg-amber-50 text-amber-700",
};

function LogbookTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/access-logs").then(({ data }) => setLogs(data)); }, []);
  return (
    <div className="max-w-3xl" data-testid="logbook-tab">
      <p className="mb-4 text-sm text-slate-500">Toegangs-logboek (365 dagen bewaard): wie logt in met welk device en vanaf welk IP-adres.</p>
      {logs.length === 0 ? <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nog geen logregels</div> : (
        <ol className="relative border-l-2 border-slate-200 pl-5">
          {logs.map((l) => (
            <li key={l.id} data-testid={`log-${l.id}`} className="mb-5 last:mb-0">
              <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-blue-500" />
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${EVENT_CLS[l.event] || "bg-slate-100 text-slate-600"}`}>{l.event}</span>
                  <span className="text-sm font-semibold text-slate-900">{l.actor}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{ROLE_LABELS[l.role] || l.role}</span>
                  <span className="ml-auto text-xs text-slate-400">{fmtTime(l.ts)}</span>
                </div>
                <div className="mt-1 grid gap-0.5 text-[11px] text-slate-500">
                  <div className="flex items-center gap-1"><Globe style={{ width: 11, height: 11 }} /> IP: {l.ip || "—"}{l.extra ? ` · ${l.extra}` : ""}</div>
                  {l.user_agent && <div className="truncate flex items-center gap-1"><Wifi style={{ width: 11, height: 11 }} /> {l.user_agent.slice(0, 80)}</div>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function BuildingDocField({ building, onChanged }) {
  const fileRef = useRef(null);
  const [docs, setDocs] = useState(building?.documents || []);
  if (!building?.id) return <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">Bewaar het gebouw eerst; daarna kun je aandachtskaart-documenten of afbeeldingen uploaden.</div>;
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/buildings/${building.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((prev) => [...prev, ...data.added]); toast.success(`${data.added.length} bestand(en) geüpload`); onChanged && onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (d) => { try { await api.delete(`/buildings/${building.id}/documents/${d.id}`); setDocs((prev) => prev.filter((x) => x.id !== d.id)); toast.success("Verwijderd"); onChanged && onChanged(); } catch (e) {} };
  return (
    <div>
      <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="building-doc-input" />
      <button type="button" data-testid="building-doc-upload" onClick={() => fileRef.current?.click()} className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white">Bestanden uploaden</button>
      {docs.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="building-doc-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <span className="truncate text-xs text-slate-700">{d.name}</span>
              <button type="button" data-testid={`building-doc-del-${d.id}`} onClick={() => remove(d)} className="ml-2 shrink-0 text-xs font-semibold text-red-600">Verwijderen</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LogoSettings({ canWrite }) {
  const { branding, reload } = useBranding();
  const fileRef = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Logo te groot (max 1.5MB)"); return; }
    const r = new FileReader();
    r.onload = async () => { try { await api.put("/settings/branding", { logo: r.result }); toast.success("Logo opgeslagen"); reload(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
    r.readAsDataURL(f);
  };
  const removeLogo = async () => { try { await api.put("/settings/branding", { logo: "" }); toast.success("Logo verwijderd"); reload(); } catch (e) {} };
  return (
    <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="logo-settings">
      <div className="text-sm font-bold text-slate-900">Logo &amp; branding</div>
      <p className="mt-1 text-sm text-slate-500">Upload het organisatielogo (gebruikt in de app-branding en in rapportages).</p>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-300">
          {branding.logo ? <img src={branding.logo} alt="logo" className="h-full w-full rounded-xl object-contain" /> : "geen"}
        </div>
        {canWrite && (
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="logo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="logo-upload" className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white">Logo uploaden</button>
            {branding.logo && <button type="button" onClick={removeLogo} className="text-xs font-semibold text-red-600">Verwijderen</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function KenmerkenInput({ value = [], onChange }) {
  const [all, setAll] = useState([]);
  const [text, setText] = useState("");
  useEffect(() => { api.get("/building-kenmerken").then(({ data }) => setAll(data)).catch(() => {}); }, []);
  const add = (v) => {
    const t = (v || "").trim(); if (!t) return;
    if (!value.some((x) => x.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setText("");
  };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));
  const suggestions = all.filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase()) && (!text || s.toLowerCase().includes(text.toLowerCase()))).slice(0, 8);
  return (
    <div data-testid="kenmerken-input">
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Kenmerken</label>
      <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 p-2">
        {value.map((k, i) => (
          <span key={i} data-testid={`kenmerk-chip-${i}`} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            {k}<button type="button" onClick={() => removeAt(i)} className="text-blue-400 hover:text-blue-700"><X style={{ width: 12, height: 12 }} /></button>
          </span>
        ))}
        <input data-testid="kenmerk-text" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(text); } }}
          placeholder="Typ een kenmerk en Enter…" className="min-w-[140px] flex-1 px-1 py-0.5 text-sm outline-none" />
      </div>
      {suggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="kenmerk-suggestions">
          {suggestions.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600 hover:border-blue-400 hover:text-blue-700">+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Beheer() {
  const { user } = useAuth();
  const canWrite = user.role === "beheerder";
  const [tab, setTab] = useState("users");
  const [data, setData] = useState({ users: [], vehicles: [], buildings: [], types: [], locations: [] });
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isCrud = ["users", "vehicles", "buildings", "types"].includes(tab);

  const load = useCallback(async () => {
    const [u, v, b, t, l] = await Promise.all([
      api.get("/users"), api.get("/vehicles"), api.get("/buildings"), api.get("/incident-types"), api.get("/locations"),
    ]);
    setData({ users: u.data, vehicles: v.data, buildings: b.data, types: t.data, locations: l.data });
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime((msg) => { if (msg && ["users_updated", "device_enrolled", "device_updated"].includes(msg.type)) load(); });

  const openNew = () => {
    const defaults = {
      users: { name: "", email: "", phone: "", role: "meldkamer", vehicle_id: "", mfa_mode: "totp", allow_mobile: true, single_device: false, location_ids: [] },
      vehicles: { name: "", callsign: "", type: "Voertuig", home_base: "", location_id: "" },
      buildings: { name: "", code: "", function: "", description: "", lat: 52.11, lng: 5.18, info_text: "", kenmerken: [], tweezijdig_aanrijden: false, approach_routes: [] },
      types: { name: "", description: "" },
    };
    setForm(defaults[tab]); setDialog({ mode: "new" });
  };
  const openEdit = (item) => { setForm({ ...item, password: "" }); setDialog({ mode: "edit", item }); };
  const endpoints = { users: "/users", vehicles: "/vehicles", buildings: "/buildings", types: "/incident-types" };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const ep = endpoints[tab];
    try {
      let payload = { ...form };
      if (tab === "buildings") { payload.lat = Number(payload.lat); payload.lng = Number(payload.lng); }
      if (tab === "users" && !payload.vehicle_id) payload.vehicle_id = null;
      if (dialog.mode === "new") { const { data } = await api.post(ep, payload); if (tab === "users" && data.enrollment_key) toast.success(`Activatiesleutel: ${data.enrollment_key} — ook per e-mail verstuurd`, { duration: 15000 }); }
      else { if (tab === "users" && !payload.password) delete payload.password; await api.put(`${ep}/${dialog.item.id}`, payload); }
      toast.success("Opgeslagen"); setDialog(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (item) => {
    if (!(await confirmDialog({ title: "Verwijderen", message: "Weet je zeker dat je dit item wilt verwijderen?", danger: true, confirmText: "Verwijderen" }))) return;
    try { await api.delete(`${endpoints[tab]}/${item.id}`); toast.success("Verwijderd"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const approveUser = async (item) => {
    try { await api.post(`/users/${item.id}/approve`); toast.success("Gebruiker goedgekeurd"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetDevice = async (item) => {
    try { await api.post(`/users/${item.id}/reset-device`); toast.success("Apparaat gereset"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resetMfa = async (item) => {
    if (!(await confirmDialog({ title: "MFA resetten", message: `MFA van ${item.name} resetten? Bij de volgende login stelt de gebruiker de authenticator opnieuw in.`, danger: true, confirmText: "Reset MFA" }))) return;
    try { await api.post(`/users/${item.id}/reset-mfa`); toast.success("MFA gereset"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [bq, setBq] = useState("");
  const rows = isCrud ? (tab === "buildings" ? data.buildings.filter((b) => !bq || (`${b.name} ${b.function || ""} ${b.code || ""}`).toLowerCase().includes(bq.toLowerCase())) : data[tab]) : [];

  return (
    <div className="space-y-5" data-testid="beheer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Beheer · Control</h1>
          <p className="text-sm text-slate-500">{canWrite ? "Masterdata, apparaten & logboek" : "Alleen-lezen (meldkamer) · apparaten & logboek beschikbaar"}</p>
        </div>
        {isCrud && canWrite && (
          <button data-testid="beheer-new-btn" onClick={openNew} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
            <Plus className="h-4 w-4" /> Toevoegen
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <t.icon style={{ width: 16, height: 16 }} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "locaties" && <LocationsTab />}
      {tab === "apparaten" && <DevicesTab />}
      {tab === "dienst" && <ShiftSettings canWrite={canWrite} />}
      {tab === "instellingen" && (<div className="space-y-5"><ConceptDoc /><AckPolicySettings canWrite={canWrite} /><LogoSettings canWrite={canWrite} /><MicSelector /></div>)}
      {tab === "callassist" && <CallAssistTab canWrite={canWrite} />}
      {tab === "omgevingen" && <EnvironmentsTab canWrite={canWrite} />}
      {tab === "audit" && <AuditLog />}
      {tab === "buildings" && <BuildingsMapEditor canWrite={canWrite} onChanged={load} />}
      {tab === "bluswater" && <WaterMapEditor canWrite={canWrite} />}
      {tab === "kaart" && (<><MapSettings canWrite={canWrite} /><ServiceAreaEditor canWrite={canWrite} /><SharedLayerEditor canWrite={canWrite} /><ColorLegendEditor canWrite={canWrite} /></>)}
      {tab === "contacten" && <ContactsTab />}
      {tab === "buildings" && (
        <input data-testid="building-search" value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Zoek op naam, functie of code…"
          className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-blue-500 md:max-w-md" />
      )}

      {isCrud && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((item) => (
            <div key={item.id} data-testid={`beheer-item-${item.id}`} className="flex items-start justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="min-w-0">
                {tab === "users" && (<><div className="font-semibold text-slate-900">{item.name}</div><div className="truncate text-xs text-slate-500">{item.email}{item.phone ? " · " + item.phone : ""}</div><div className="mt-1 flex flex-wrap gap-1"><span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{ROLE_LABELS[item.role]}</span>{item.status === "active" && <span className="inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">actief</span>}{item.status === "pending_key" && <span className="inline-block rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">wacht op activatie</span>}{item.status === "pending_approval" && <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">wacht op goedkeuring</span>}{item.mfa_mode && item.mfa_mode !== "off" && <span className="inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">MFA {item.mfa_mode}</span>}{item.allow_mobile === false && <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">geen mobiel</span>}{item.single_device && <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">1 apparaat</span>}</div></>)}
                {tab === "vehicles" && (<><div className="font-mono font-semibold text-slate-900">{item.callsign}</div><div className="text-sm text-slate-600">{item.name}</div><div className="text-xs text-slate-400">{item.type} · {item.home_base}{item.location_id && data.locations?.find((l) => l.id === item.location_id) ? ` · ${data.locations.find((l) => l.id === item.location_id).name}` : ""}</div></>)}
                {tab === "buildings" && (<><div className="font-semibold text-slate-900">{item.name}</div>{item.function && <div className="text-xs font-medium text-blue-600">{item.function}</div>}<div className="text-xs text-slate-500">{item.code} · {item.lat}, {item.lng}</div><div className="text-xs text-slate-400">{item.description}</div>{item.kenmerken?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{item.kenmerken.slice(0, 5).map((k, i) => { const M = kenmerkMeta(k); return <span key={i} className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium" style={{ color: M.color }}><M.Icon style={{ width: 10, height: 10 }} /> {k}</span>; })}</div>}{(item.documents?.length || item.doc_name) && <div className="mt-1 text-[10px] font-semibold text-amber-600">📎 aandachtskaart{item.documents?.length ? ` (${item.documents.length})` : ""}</div>}</>)}
                {tab === "types" && (<><div className="font-semibold text-slate-900">{item.name}</div><div className="text-xs text-slate-400">{item.description}</div></>)}
              </div>
              {canWrite && (
                <div className="flex shrink-0 gap-1">
                  {tab === "users" && item.status === "pending_approval" && <button data-testid={`approve-${item.id}`} onClick={() => approveUser(item)} className="rounded-lg p-1.5 text-emerald-500 hover:bg-emerald-50" title="Goedkeuren"><Check className="h-4 w-4" /></button>}
                  {tab === "users" && item.single_device && item.bound_device_fp && <button data-testid={`resetdev-${item.id}`} onClick={() => resetDevice(item)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" title="Apparaat resetten"><Smartphone className="h-4 w-4" /></button>}
                  {tab === "users" && item.mfa_mode === "totp" && <button data-testid={`resetmfa-${item.id}`} onClick={() => resetMfa(item)} className="rounded-lg p-1.5 text-slate-400 hover:bg-amber-50 hover:text-amber-600" title="MFA/authenticator resetten"><KeyRound className="h-4 w-4" /></button>}
                  <button data-testid={`edit-${item.id}`} onClick={() => openEdit(item)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-${item.id}`} onClick={() => remove(item)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 p-4" onClick={() => setDialog(null)}>
          <div data-testid="beheer-dialog" className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="font-display font-bold text-slate-900">{dialog.mode === "new" ? "Toevoegen" : "Bewerken"}</h3>
              <button onClick={() => setDialog(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-3 p-6">
              {tab === "users" && (<>
                <Field label="Naam" data-testid="f-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label="E-mail" data-testid="f-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Field label="Telefoonnummer" data-testid="f-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                {dialog.mode === "new"
                  ? <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">De gebruiker krijgt een unieke activatiesleutel per e-mail, stelt zelf een wachtwoord in en wordt daarna door jou goedgekeurd.</div>
                  : <Field label="Nieuw wachtwoord (leeg = ongewijzigd)" data-testid="f-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">MFA (tweede factor)</label>
                  <select data-testid="f-mfa" value={form.mfa_mode || "totp"} onChange={(e) => setForm({ ...form, mfa_mode: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                    <option value="totp">Authenticator-app (TOTP)</option><option value="email">E-mailcode</option><option value="off">Uit (excluded)</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="f-allow-mobile" type="checkbox" checked={form.allow_mobile !== false} onChange={(e) => setForm({ ...form, allow_mobile: e.target.checked })} className="h-4 w-4 accent-blue-600" /> Mag op mobiel/tablet inloggen</label>
                <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="f-single-device" type="checkbox" checked={!!form.single_device} onChange={(e) => setForm({ ...form, single_device: e.target.checked })} className="h-4 w-4 accent-blue-600" /> Vastzetten op één apparaat</label>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Rol</label>
                  <select data-testid="f-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                    <option value="meldkamer">Meldkamer</option><option value="voertuig">Voertuig</option><option value="beheerder">Beheerder</option>
                  </select>
                </div>
                {form.role === "voertuig" && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Gekoppeld voertuig</label>
                    <select data-testid="f-vehicle" value={form.vehicle_id || ""} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                      <option value="">— kies voertuig —</option>
                      {data.vehicles.map((v) => <option key={v.id} value={v.id}>{v.callsign} · {v.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Locaties (voor pager-alarmering)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {(data.locations || []).length === 0 ? <span className="text-xs text-slate-400">Nog geen locaties — maak ze aan in het tabblad Locaties.</span> :
                      data.locations.map((l) => {
                        const sel = (form.location_ids || []).includes(l.id);
                        return (
                          <button type="button" key={l.id} data-testid={`f-loc-${l.id}`}
                            onClick={() => setForm((f) => { const cur = f.location_ids || []; return { ...f, location_ids: cur.includes(l.id) ? cur.filter((x) => x !== l.id) : [...cur, l.id] }; })}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${sel ? "bg-blue-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                            {l.name}
                          </button>
                        );
                      })}
                  </div>
                </div>
              </>)}
              {tab === "vehicles" && (<>
                <Field label="Roepnaam / callsign" data-testid="f-callsign" required value={form.callsign} onChange={(e) => setForm({ ...form, callsign: e.target.value })} />
                <Field label="Naam" data-testid="f-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label="Type" data-testid="f-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
                <Field label="Thuisbasis" data-testid="f-base" value={form.home_base} onChange={(e) => setForm({ ...form, home_base: e.target.value })} />
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Locatie / kazerne</label>
                  <select data-testid="f-vehicle-location" value={form.location_id || ""} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500">
                    <option value="">— geen locatie —</option>
                    {(data.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </>)}
              {tab === "buildings" && (<>
                <Field label="Naam" data-testid="f-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label="Functie van gebouw" data-testid="f-function" value={form.function || ""} onChange={(e) => setForm({ ...form, function: e.target.value })} />
                <Field label="Code" data-testid="f-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                <Field label="Omschrijving" data-testid="f-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Lat" data-testid="f-lat" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} />
                  <Field label="Lng" data-testid="f-lng" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Aandachtskaart-tekst</label>
                  <textarea data-testid="f-info" rows={3} value={form.info_text || ""} onChange={(e) => setForm({ ...form, info_text: e.target.value })} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Bijv. aansluitingen, sleutelkluis, bijzonderheden…" />
                </div>
                <KenmerkenInput value={form.kenmerken || []} onChange={(k) => setForm({ ...form, kenmerken: k })} />
                <label data-testid="f-tweezijdig" className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <input type="checkbox" checked={!!form.tweezijdig_aanrijden} onChange={(e) => setForm({ ...form, tweezijdig_aanrijden: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">Tweezijdig aanrijden</span>
                    <span className="block text-xs text-slate-500">Toon een waarschuwing bij meldingen op deze locatie: aanrijden vanaf twee zijden.</span>
                  </span>
                </label>
                {form.tweezijdig_aanrijden && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Aanrijroutes (twee zijden)</label>
                    <ApproachRouteEditor center={{ lat: Number(form.lat), lng: Number(form.lng) }} value={form.approach_routes || []} onChange={(r) => setForm({ ...form, approach_routes: r })} />
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Aandachtskaart-document (PDF/afbeelding)</label>
                  <BuildingDocField building={dialog.item} onChanged={load} />
                </div>
              </>)}
              {tab === "types" && (<>
                <Field label="Naam" data-testid="f-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label="Omschrijving" data-testid="f-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </>)}
              <button data-testid="beheer-save" type="submit" disabled={saving} className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">Opslaan</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
